import { resetCurrentWorkspace } from 'app-redux/actions/licensingAccounts.actions';
import { AppState } from 'app-redux/reducers';
import { getCurrentUser } from 'app-redux/selectors/accounts.selector';
import { getDefaultWorkspace, getSelectedWorkspaceObject } from 'app-redux/selectors/licensingAccounts.selector';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, ButtonType, Spin } from 'syngenta-digital-cropwise-react-ui-kit';
import { find, get } from 'lodash';
import fallBackImage from 'assets/images/emptyImage.svg';
import { PlusOutlined } from '@ant-design/icons';
import MemberIcon from 'assets/icons/member.svg';
import PlanIcon from 'assets/icons/plan.svg';
import NoUserFoundImage from 'assets/images/no_user_found.svg';
import NoPlanIcon from 'assets/images/no_plan_found.svg';
import { AppCard } from '@cw-elements/design';

// assets
import DefaultProductImage from 'assets/images/default_product_background.png';

import './styles.less';
import { EmptyStateWorkspaceContainer, NO_PLANS_FOUND, WORKSPACE_NOT_FOUND } from './EmptyStateWorkspaceContainer';
import { Avatar, Badge, ConfigProvider, Table } from 'antd';
import { getOrgsAndProperties, getWorkspaceAccounts } from 'services/MemberService';
import { processMemberAndRoles } from 'pages/Members';
import {
  getContractCurrentStatus,
  isPermissionAccess,
  redirectToActivateNewPlanDeepAction,
  translationWrapper,
  isUK,
  checkOrgOwnership,
  checkUserPermissionWithOrgsOwnerShip,
  redirectToInviteMemberMFeOrDeepAction,
} from 'utils/common-methods';
import { history, RoutePaths } from 'core/history';
import moment from 'moment';
import { UUID } from 'core/utils/BasicModels';
import { getWorkspaceById } from 'app-redux/actions/workspace.actions';
import { getPlanById } from 'app-redux/actions/plans.actions';
import { getAppPublicInfoById } from 'app-redux/actions/storeApps.actions';
import { useTranslation } from 'react-i18next';
import { TryItApps } from 'components/TryItApps';
import { getDomain } from 'core/common-methods';
import { getBrandName } from 'utils/BrandingUtils';
import { getOrgPropertyRBACActions, verifyRBACPermission } from 'services/rbac';
import { RBAC_ACTIONS } from 'services/rbacActions';
import { ButtonToolTip } from 'components/Buttons/RBACButtonTooltip';
import { AppConstants } from 'core/app.constants';
import { Img } from 'react-image';
import { useMediaQuery } from 'react-responsive';
import { iff } from 'core/iff';
import { getCurrentOrg } from 'app-redux/selectors/orgs.selector';
import { ContractDTO } from '../../core/contracts/models';

const OverviewHeader = (props: any) => {
  const { t = (key: string) => key, userName = '' } = props;
  const subtitle = t('text.hello') + userName + '! ' + t(`${isUK() ? 'text.manage_your_members_and_plans_to_use_your_uk' : 'text.manage_your_members_and_plans_to_use_your_cropwise'}`);
  const title = t(`${isUK() ? 'text.your_cropwise_account_uk' : 'text.your_cropwise_account'}`);
  return <>
    <div className="overview-header-container">

      <p className="title">
        {title}
      </p>
      <p className="subtitle">
        {subtitle}
      </p>

    </div>
  </>;
};

interface Product {
  updated_at: string;
  last_updated_by_id: string;
  name: string;
  short_description: string;
  detailed_description: string;
  type: any;
  supported_apps: any;
  published: boolean;
  site_uri: string;
  apple_store_uri: string;
  play_store_uri: string;
  icon_uri: string;
  image_uri: string;
  id: string;
}

const ProductCards = (props: any) => {
  const platformProducts = props.products.filter((product: Product) => product.id !== AppConstants.FARM_SETTINGS_ID);
  const { t = (key: string) => key, userName = '' } = props;

  return useMemo(() => {
    const redirectToApp = (siteUri: string) => {
      const workspaceId = props.currentWorkspaceId;
      const orgId = props.currentOrg.id;
      const url = siteUri + (workspaceId && orgId ? `?workspace=${workspaceId}&organization=${orgId}` : '');

      window.open(url, '_blank')?.focus();
    };

    const appCard = (product: Product, image: string, siteUri: string, isMobile: boolean) => {
      return (
        <AppCard
          apple_store_uri={isMobile && product.apple_store_uri ? product.apple_store_uri : ''}
          background={image}
          name={product.name}
          logo={product.icon_uri}
          detailed_description={product.detailed_description}
          link={siteUri}
          onClick={() => redirectToApp(siteUri)}
          play_store_uri={isMobile && product.play_store_uri ? product.play_store_uri : ''}
          darkMode={false}
          key={props.currentWorkspaceId}
          orgId={props.currentOrg?.id}
          propertyId=""
          workspaceId={props.currentWorkspaceId}
          textAbout={t(`${'app_card.modal.about'}`)}
          textMobile={t(`${'app_card.modal.mobile'}`)}
          textCopyLinkButton={t(`${'app_card.modal.copy_link'}`)}
          textCopiedLink={t(`${'app_card.modal.link_copied'}`)}
          textOkButton={t(`${'app_card.ok'}`)}
          textQRCode={t(`${'app_card.scan_the_qr_code_with_your_phones_camera_or_click_below_to_copy_link'}`)}
          textDiscoverAndDownloadTooltip={t(`${'app_card.discover_and_download'}`)}
          textOpenInNewTabTooltip={t(`${'app_card.open_in_new_tab'}`)} />
      );
    };

    return <div className="productListContainer">
      {
        platformProducts.map((product: Product, index: number) => {
          const siteUri = product?.site_uri;
          const image = product?.image_uri || DefaultProductImage;

          return (
            <Fragment key={product.id}>
              {!!(
                product.apple_store_uri !== null ||
                product.play_store_uri !== null
              ) && (
                <div className="component-app-card-container">
                  {appCard(product, image, siteUri, true)}
                </div>
              )}
              {siteUri && (
                <div className="component-app-card-container">
                  {appCard(product, image, siteUri, false)}
                </div>
              )}
            </Fragment>
          );
        })
      }
    </div>;
  }, [props.products]);
};

const TableCardHeader = (props: any) => {
  const { title = '', buttonProps = {}, leftIcon, headerActionPermission, extraParams } = props;
  const { headerActionPermissionTooltip } = extraParams;
  return <>
    <div className="table-card-hedaer-container">
      <div className="left-container">
        <img src={leftIcon}/>
        {title}
      </div>
      <div>
        <ButtonToolTip disabled={!headerActionPermission} disabledButtonTitle={headerActionPermissionTooltip} >
          <Button
            className="products-page-activate-new-plan-btn"
            type={ButtonType.outline}
            size="middle"
            icon={<PlusOutlined />}
            onClick={headerActionPermission ? buttonProps.click : () => false}
            style={!headerActionPermission ? {
              background: 'whitesmoke',
              color: '#c1c5c8',
              cursor: 'not-allowed',
            } : {}}
          >
            <span>{buttonProps.label}</span>
          </Button>
        </ButtonToolTip>
      </div>
    </div>
  </>;
};

const TableCard = (props: any) => {
  const { emptyState, columns, headerActionPermission = false, footerProps = {}, header = {}, list, title, loading, t = (key: string) => key } = props;
  const { leftIcon, buttonProps } = header;
  const { extraParams = {} } = props;

  return <>
    <div className="table-card-container">

      <Spin spinning={loading}>
        <TableCardHeader extraParams={extraParams} headerActionPermission={headerActionPermission} leftIcon={leftIcon} title={title} buttonProps={{ label: buttonProps.label, click: buttonProps.click }}/>
        <ConfigProvider renderEmpty={emptyState} >
          <Table
            rowKey={(record: any) => record?.id ?? ''}
            columns={columns}
            dataSource={list}
            pagination={false}
          />
        </ConfigProvider>
        <div className="table-card-footer-container">
          {
            list.length > 0 ? <Button
              className="table-card-footer-button"
              type={ButtonType.outline}
              size="middle"
              onClick={footerProps.click}
            >
              {t('button.see_all')}
            </Button> : <></>
          }
        </div>
      </Spin>
    </div>
  </>;
};

export const customizeRenderEmpty = (props: any) => {
  const { t = (key: string) => key, title, subtitle, icon } = props;
  return (
    <div className="overview-data-empty-container" style={{ textAlign: 'center' }}>
      <img className="image" src={icon} alt="No member Found"/>
      <span className="empty-data-title">{title}</span>
      <span className="empty-data-subtitle">{subtitle}</span>
    </div>
  );
};

const MemberRolesComponent = (props: any) => {
  const { roles } = props;
  const { t } = useTranslation();
  if (!!roles.length && !!roles[0].name) {
    return (
      <>
        {iff(roles.length > 1,
          <div className="roles-value">
            {t(`rbac_roles.${roles[0].role_id}.name`)}
            <div className="extra-role-count">{roles.length - 1}+</div>
          </div>,
          <div style={{ color: '#4D5165' }}>
            {t(`rbac_roles.${roles[0].role_id}.name`)}
          </div>,
        )}
      </>
    );
  } return <></>;
};

export const OverviewContainer = (props: any) => {
  const { workspaceLoading, workspaces = [], currentWorkspaceId, currentOrg, currentUser, orgs } = props;
  const isMobile = useMediaQuery({ query: '(max-width: 719px)' });
  const currentSelectedProduct = find(workspaces, { id: currentWorkspaceId }) || {};
  const { t } = translationWrapper(useTranslation());
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [rbacPermissions, setRbacPermissions] = useState<any>([]);
  const [emptyState, setEmptyState] = useState<any>([]);
  const [loadingData, setLoadingData] = useState({ plan: true, member: true, workspace: true, other: false });
  const date_format = props.currentUser?.date_format || 'MM/DD/YYYY';
  const currentOrgId = get(currentOrg, 'id', undefined);
  const redirectToMember = () => {
    history.push({ pathname: RoutePaths.MEMBERS().toString(), ...{ search: `?workspace=${currentWorkspaceId}&organization=${currentOrgId}` }, state: {} });
  };
  const redirectToPlan = () => {
    history.push({ pathname: RoutePaths.PLANS().toString(), ...{ search: `?workspace=${currentWorkspaceId}&organization=${currentOrgId}` }, state: {} });
  };

  const orgsInCurrentWorkspace = orgs?.flatMap((org: any) =>
    org.workspace_id === currentWorkspaceId ? [org.id] : [],
  );
  async function getMembers() {
    setLoadingData((preState: any) => {
      return { ...preState, member: true };
    });
    setMembers([]);
    const workspaceAccountsResponse: any = await getWorkspaceAccounts(currentWorkspaceId, orgsInCurrentWorkspace, { size: 5 });
    let processedData: any = [];
    if (workspaceAccountsResponse.roles.length) {
      processedData = processMemberAndRoles(workspaceAccountsResponse.members, workspaceAccountsResponse.roles, currentWorkspaceId);
    } else {
      processedData = workspaceAccountsResponse.members;
    }
    if (workspaceAccountsResponse.status) {
      setMembers(processedData.content);
    }
    setLoadingData((preState: any) => {
      return { ...preState, member: false };
    });
  }

  const checkHeaderActionPermissionForMembers = () => {
    if (checkUserPermissionWithOrgsOwnerShip() && currentOrgId && currentUser) {
      return checkOrgOwnership(currentUser?.authorities, ['WRITE', 'READ'], currentOrgId);
    } else {
      return rbacPermissions[RBAC_ACTIONS.member.new] && props.isInviteMembersEnabledOnFront;
    }
  };

  const getProductsByContracts: any = async (contracts = []) => {
    try {
      const appDataPromiseResponse = await Promise.all(
        contracts.map((plansResponse: any) =>
          props.getAppPublicInfoById(plansResponse.app_id)
            .catch((err: any) => {
              console.error('Could not get public app info: ', err);
            }))
          .filter((appData: any) => appData),
      );
      return contracts.reduce((acc: unknown[] , cnt: ContractDTO) => {
        const appData: any = find(appDataPromiseResponse, { id: cnt.app_id });
        if (appData) {
          acc.push({
            product: appData.name,
            icon_uri: appData.icon_uri,
            // plan: cnt.plan_name,
            start_date: cnt.start,
            expiration_date: cnt.end,
            waiting_for_payment: cnt.waiting_for_payment,
            id: cnt.id,
            contract_status: getContractCurrentStatus(cnt),
          });
        }
        return acc;
      }, []);
    } catch (r) {
      return get(r, 'message', 'Request failed');
    }
  };

  const fetchPlans = async () => {
    setLoadingData((preState: any) => {
      return { ...preState, plan: true };
    });
    if (currentWorkspaceId) {
      const workspace = await props.getWorkspaceById(currentWorkspaceId);
      const contracts = workspace.contracts || [];
      if (contracts && contracts.length) {
        const productsFetched: any = await getProductsByContracts(contracts);
        if (typeof productsFetched === 'string') {
          // display error message if required
        } else {
          if (productsFetched.length > 0) {
            setPlans(productsFetched.slice(0, 5));
          } else {
            setEmptyState((preState: any) => {
              return [...preState, NO_PLANS_FOUND];
            });
          }
        }
      } else {
        setEmptyState((preState: any) => {
          return [...preState, NO_PLANS_FOUND];
        });
      }
    }
    setLoadingData((preState: any) => {
      return { ...preState, plan: false };
    });
  };

  useEffect(() => {
    if (currentWorkspaceId && props.currentUser) {
      setEmptyState([]);
      getRBACPermissions();
    }
  }, [currentWorkspaceId, props.currentUser]);

  useEffect(() => {
    if (!workspaceLoading) {
      if (workspaces.length == 0) {
        setEmptyState((preState: any) => {
          return [...preState, WORKSPACE_NOT_FOUND];
        });
      }
    }
  }, [workspaceLoading]);

  const memberColumns: any = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      render: (_text: string, record: any) => {
        return <>
          <div className="table-card-member-column-container">
            <Avatar size="small">{record.name.slice(0, 2).toUpperCase()}</Avatar>
            <span className="name-value">{record.name} </span>
            {
              record.roles ? <div style={{ display: 'flex', marginLeft: '5px' }}>{' · '} <MemberRolesComponent roles={record.roles}/> </div> : <></>
            }
          </div>
        </>;
      },
    },
  ];
  const plansColumn: any = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      render: (_text: string, product: any) => {
        return <>
          <div className="table-card-plan-column-container">
            <Img className="app-icon" src={[product.icon_uri, fallBackImage]} />
            <span className="name-value">{product.product} </span>
            {
              product.expiration_date ? <span className="roles-value">· Expires {moment(product.expiration_date).format(date_format)}</span> : <></>
            }
          </div>
        </>;
      },
    },
    {
      title: t('Status'),
      dataIndex: 'contract_status',
      key: 'contract_status',
      filters: [
        {
          text: t('global.status.active'),
          value: 'active',
        },
        {
          text: t('global.status.inactive'),
          value: 'inactive',
        },
        {
          text: t('global.status.pending'),
          value: 'pending',
        },
      ],
      onFilter: (value: any, record: any) => value === record.contract_status,
      render: (text: string) => {
        if (text === 'inactive') {
          return (
            <div className="product_badge_card">
              {' '}
              <Badge status="default" text={t('global.status.inactive')} className="product_badge_card__inactive" />
            </div>
          );
        }
        if (text === 'pending') {
          return (
            <div className="product_badge_card">
              {' '}
              <Badge status="processing" text={t('global.status.pending')} className="product_badge_card__pending" />
            </div>
          );
        }
        return (
          <div className="product_badge_card">
            {' '}
            <Badge status="success" text={t('global.status.active')} className="product_badge_card__active" />
          </div>
        );
      },
    },
  ];

  const memberCardParmas = {
    headerActionPermissionTooltip: 'You don’t have access to this page.',
  };
  const planCardParmas = {
    headerActionPermissionTooltip: 'You don’t have access to this page.',
  };

  const [memberTableEmptyParams, setMemberTableEmptyParams] = useState({
    title: t('global.info.no_members_yet'),
    subtitle: (getBrandName() === 'Cropwise' ? t('global.info.invite_subtitle_cropwise') : t('global.info.invite_subtitle_digital')),
    icon: NoUserFoundImage,
    t,
  });
  const planTableEmptyParams = {
    title: t('global.info.sorry_you_dont_have_access_to_your_products_plans'),
    subtitle: '',
    icon: NoPlanIcon,
    t,
  };

  const checkUserPermissionsForMembers = (permission: any) => {
    if (permission) {
      getMembers();
    } else {
      setLoadingData((preState: any) => {
        return { ...preState, member: false };
      });
      setMemberTableEmptyParams({
        title: t('global.info.sorry_you_cannot_view_your_workspace_members'),
        subtitle: '',
        icon: NoUserFoundImage,
        t,
      });
    }
  };

  const getRBACPermissions = async () => {
    const extraRbacOperations =
      [
        {
          action: RBAC_ACTIONS.member.new,
          resource: `crn:workspace:${currentWorkspaceId}`,
        },
        {
          action: RBAC_ACTIONS.contract.new,
          resource: `crn:workspace:${currentWorkspaceId}`,
        },
      ];
    setLoadingData((preState: any) => {
      return { ...preState, other: true };
    });
    const orgPropertyData = await getOrgsAndProperties(currentWorkspaceId);
    const rbacPermissionsPayload = getOrgPropertyRBACActions({ ...{ currentWorkspaceId }, ...orgPropertyData });
    rbacPermissionsPayload.operations = rbacPermissionsPayload.operations.concat(extraRbacOperations);
    const { data: { allowed_operations } }: any = await verifyRBACPermission(rbacPermissionsPayload);
    setLoadingData((preState: any) => {
      return { ...preState, other: false };
    });
    const rbacPermissionsObj: any = {};
    allowed_operations.forEach((permission: any) => {
      rbacPermissionsObj[permission.action] = true;
    });
    setRbacPermissions(rbacPermissionsObj);

    if (checkUserPermissionWithOrgsOwnerShip()) {
      checkUserPermissionsForMembers(checkOrgOwnership(currentUser?.authorities, ['READ'], currentOrgId));
    } else {
      checkUserPermissionsForMembers(isPermissionAccess(allowed_operations, RBAC_ACTIONS.member.read));
    }

    fetchPlans().catch();
  };

  return (
    <Spin spinning={loadingData.other || workspaceLoading} tip="Loading...">
      {/* Overview content */}
      {emptyState.length === 0 ? (
        <div className="overview-page-container">
          <OverviewHeader
            t={t}
            userName={props.currentUser?.name}
          />
          {currentSelectedProduct.products && (
            <>
              <ProductCards
                currentWorkspaceId={currentWorkspaceId}
                products={currentSelectedProduct.products}
                isMobile={isMobile}
                currentOrg={currentOrg}
                t={t}
              />
              <TryItApps
                currentWorkspaceId={currentWorkspaceId}
                products={currentSelectedProduct.products}
              />
            </>
          )}
          <div className="member-plan-card-container">
            <div className="member-card-container">
              <TableCard
                extraParams={memberCardParmas}
                emptyState={() => customizeRenderEmpty(memberTableEmptyParams)}
                t={t}
                headerActionPermission={checkHeaderActionPermissionForMembers()}
                loading={loadingData.member}
                footerProps={{ click: redirectToMember }}
                title={t('global.title.members')}
                list={members}
                columns={memberColumns}
                header={{
                  leftIcon: MemberIcon,
                  buttonProps: {
                    label: t('button.invite_member'),
                    click: () => redirectToInviteMemberMFeOrDeepAction(currentWorkspaceId),
                  },
                }}
              />
            </div>
            <div className="plan-card-container">
              <TableCard
                extraParams={planCardParmas}
                emptyState={() => customizeRenderEmpty(planTableEmptyParams)}
                t={t}
                headerActionPermission={true}
                loading={loadingData.plan}
                footerProps={{ click: redirectToPlan }}
                title={t('global.title.plans')}
                list={plans}
                columns={plansColumn}
                header={{
                  leftIcon: PlanIcon,
                  buttonProps: {
                    label: t('button.activate_plan'),
                    click: redirectToActivateNewPlanDeepAction,
                  },
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="overview-page-container">
          <EmptyStateWorkspaceContainer
            createPlanPermission={rbacPermissions[RBAC_ACTIONS.contract.new]}
            state={emptyState}
            {...props}
          />
        </div>
      )}
    </Spin>
  );
};

const mapStateToProps = (state: AppState, ownProps: any) => ({
  currentUser: getCurrentUser(state),
  currentWorkspaceId: getDefaultWorkspace(state),
  workspaces: get(state, 'workspaces.workspaces', []),
  workspacesLength: get(state, 'workspaces.workspaces.length', 0),
  accountId: get(ownProps, 'match.params.accountId', null),
  currentWorkspace: getSelectedWorkspaceObject(state),
  workspaceLoading: get(state, 'workspaces.loading', true),
  enableCreateWorkspaceOnAccountMng: get(state, 'rollout.flags.enableCreateWorkspaceOnAccountMng', false),
  isInviteMembersEnabledOnFront: state.rollout.flags.isInviteMembersEnabledOnFront,
  currentOrg: getCurrentOrg(state),
  orgs: get(state, 'orgsState.orgs', []),
});

const mapDispatchToProps = (dispatch: any) => ({
  resetCurrentWorkspace: () => dispatch(resetCurrentWorkspace()),
  getPlanById: (planId: string) => dispatch(getPlanById(planId)),
  getWorkspaceById: (workspaceId: UUID) => dispatch(getWorkspaceById(workspaceId)),
  getAppPublicInfoById: (appId: UUID) => dispatch(getAppPublicInfoById(appId)),
});

export const OverviewPage = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OverviewContainer),
);
